import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../../../services/adminApi';
import { toast } from 'react-hot-toast';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';

const LocationForm = ({ location, onSuccess, onCancel }) => {
  const isEdit = Boolean(location);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    taxInvoiceEntityCode: '',
    type: 'branch',
    operatingMode: 'owned',
    parentLocation: '',
    description: '',
    partner: {
      legalName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      agreementRef: '',
      bank: {
        bankName: '',
        branchName: '',
        accountName: '',
        accountNumber: '',
        bankCode: ''
      },
      cardCollectionCoa: {
        accountCode: '',
        accountName: ''
      }
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      coordinates: {
        latitude: '',
        longitude: ''
      }
    },
    contact: {
      phone: '',
      email: '',
      website: '',
      manager: {
        name: '',
        email: '',
        phone: ''
      },
      alternatePhone: ''
    },
    capacity: {
      totalSpace: '',
      usedSpace: '',
      availableSpace: '',
      unit: 'sqft',
      maxWeight: '',
      weightUnit: 'kg'
    },
    settings: {
      isActive: true,
      isDefault: false,
      allowNegativeStock: false,
      autoReorderEnabled: false,
      temperatureControlled: false,
      hazardousMaterials: false
    },
    zones: [],
    notes: ''
  });

  useEffect(() => {
    // Load all locations for parent dropdown
    loadLocations();
  }, []);

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name || '',
        code: location.code || '',
        taxInvoiceEntityCode: location.taxInvoiceEntityCode || '',
        type: location.type || 'branch',
        operatingMode: location.operatingMode || (location.type === 'franchise_center' ? 'franchise' : 'owned'),
        parentLocation: location.parentLocation?._id || location.parentLocation || '',
        description: location.description || '',
        partner: {
          legalName: location.partner?.legalName || '',
          contactName: location.partner?.contactName || '',
          contactEmail: location.partner?.contactEmail || '',
          contactPhone: location.partner?.contactPhone || '',
          agreementRef: location.partner?.agreementRef || '',
          bank: {
            bankName: location.partner?.bank?.bankName || '',
            branchName: location.partner?.bank?.branchName || '',
            accountName: location.partner?.bank?.accountName || '',
            accountNumber: location.partner?.bank?.accountNumber || '',
            bankCode: location.partner?.bank?.bankCode || ''
          },
          cardCollectionCoa: {
            accountCode: location.partner?.cardCollectionCoa?.accountCode || '',
            accountName: location.partner?.cardCollectionCoa?.accountName || ''
          }
        },
        address: {
          street: location.address?.street || '',
          city: location.address?.city || '',
          state: location.address?.state || '',
          zipCode: location.address?.zipCode || '',
          country: location.address?.country || 'USA',
          coordinates: {
            latitude: location.address?.coordinates?.latitude || '',
            longitude: location.address?.coordinates?.longitude || ''
          }
        },
        contact: {
          phone: location.contact?.phone || '',
          email: location.contact?.email || '',
          website: location.contact?.website || '',
          manager: {
            name: location.contact?.manager?.name || '',
            email: location.contact?.manager?.email || '',
            phone: location.contact?.manager?.phone || ''
          },
          alternatePhone: location.contact?.alternatePhone || ''
        },
        capacity: {
          totalSpace: location.capacity?.totalSpace?.toString() || '',
          usedSpace: location.capacity?.usedSpace?.toString() || '',
          availableSpace: location.capacity?.availableSpace?.toString() || '',
          unit: location.capacity?.unit || 'sqft',
          maxWeight: location.capacity?.maxWeight?.toString() || '',
          weightUnit: location.capacity?.weightUnit || 'kg'
        },
        settings: {
          isActive: location.settings?.isActive !== undefined ? location.settings.isActive : true,
          isDefault: location.settings?.isDefault || false,
          allowNegativeStock: location.settings?.allowNegativeStock || false,
          autoReorderEnabled: location.settings?.autoReorderEnabled || false,
          temperatureControlled: location.settings?.temperatureControlled || false,
          hazardousMaterials: location.settings?.hazardousMaterials || false
        },
        zones: location.zones || [],
        notes: location.notes || ''
      });
    }
  }, [location]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const loadLocations = async () => {
    try {
      const response = await adminAPI.getLocations({ limit: 1000, status: 'active' });
      const allLocations = response.data.data || [];
      
      // Filter out the current location (for edit mode) to prevent circular reference
      const filteredLocations = isEdit 
        ? allLocations.filter(loc => loc._id !== location._id)
        : allLocations;
      
      setLocations(filteredLocations);
    } catch (error) {
      console.error('Failed to load locations:', error);
      toast.error('Failed to load parent locations');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Location code is required';
    } else if (!/^[A-Z0-9\-_]{2,20}$/.test(formData.code)) {
      newErrors.code = 'Code must be 2-20 characters, uppercase alphanumeric with dashes/underscores';
    }

    const entityCode = String(formData.taxInvoiceEntityCode || '').trim().toUpperCase();
    if (entityCode && !/^[A-Z0-9]{2,15}$/.test(entityCode)) {
      newErrors.taxInvoiceEntityCode = 'Entity code must be 2-15 uppercase alphanumeric characters';
    }

    // Email validations
    if (formData.contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.email)) {
      newErrors['contact.email'] = 'Valid email is required';
    }
    if (formData.contact.manager.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.manager.email)) {
      newErrors['contact.manager.email'] = 'Valid manager email is required';
    }

    // Capacity validations for warehouse types
    const warehouseTypes = ['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'];
    if (warehouseTypes.includes(formData.type)) {
      if (formData.capacity.totalSpace && parseFloat(formData.capacity.totalSpace) < 0) {
        newErrors['capacity.totalSpace'] = 'Total space cannot be negative';
      }
      if (formData.capacity.usedSpace && parseFloat(formData.capacity.usedSpace) < 0) {
        newErrors['capacity.usedSpace'] = 'Used space cannot be negative';
      }
      if (formData.capacity.maxWeight && parseFloat(formData.capacity.maxWeight) < 0) {
        newErrors['capacity.maxWeight'] = 'Max weight cannot be negative';
      }
    }

    // Description validation
    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    // Notes validation
    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value
          }
        }));
      } else if (parts.length === 3) {
        const [parent, child, grandchild] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [grandchild]: type === 'checkbox' ? checked : value
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (name === 'code' || name === 'taxInvoiceEntityCode' ? value.toUpperCase() : value)
      }));
    }
  };

  const handleWarehouseSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('capacity.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        capacity: {
          ...prev.capacity,
          [field]: value
        }
      }));
    } else if (name.startsWith('settings.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix all validation errors');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Clean up data to match backend schema
      const submitData = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        taxInvoiceEntityCode: formData.taxInvoiceEntityCode.trim().toUpperCase() || undefined,
        type: formData.type,
        parentLocation: formData.parentLocation || undefined,
        description: formData.description,
        address: {
          street: formData.address.street,
          city: formData.address.city,
          state: formData.address.state,
          zipCode: formData.address.zipCode,
          country: formData.address.country,
          coordinates: {
            latitude: parseFloat(formData.address.coordinates.latitude) || undefined,
            longitude: parseFloat(formData.address.coordinates.longitude) || undefined
          }
        },
        contact: {
          phone: formData.contact.phone,
          email: formData.contact.email,
          website: formData.contact.website,
          manager: {
            name: formData.contact.manager.name,
            email: formData.contact.manager.email,
            phone: formData.contact.manager.phone
          },
          alternatePhone: formData.contact.alternatePhone
        },
        settings: formData.settings,
        notes: formData.notes
      };

      // Only include capacity if type is warehouse
      const warehouseTypes = ['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'];
      if (warehouseTypes.includes(formData.type)) {
        submitData.capacity = {
          totalSpace: parseFloat(formData.capacity.totalSpace) || 0,
          usedSpace: parseFloat(formData.capacity.usedSpace) || 0,
          availableSpace: parseFloat(formData.capacity.availableSpace) || 0,
          unit: formData.capacity.unit,
          maxWeight: parseFloat(formData.capacity.maxWeight) || 0,
          weightUnit: formData.capacity.weightUnit
        };
        submitData.zones = formData.zones;
      }

      if (isEdit) {
        await adminAPI.updateLocation(location._id, submitData);
        toast.success('Location updated successfully');
      } else {
        await adminAPI.createLocation(submitData);
        toast.success('Location created successfully');
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.message || 'Failed to save location');
    } finally {
      setSubmitting(false);
    }
  };

  const typeOptions = [
    { value: 'headquarters', label: 'Headquarters' },
    { value: 'branch', label: 'Branch' },
    { value: 'franchise_center', label: 'Franchise Center' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'factory', label: 'Factory' },
    { value: 'office', label: 'Office' },
    { value: 'distribution_center', label: 'Distribution Center' },
    { value: 'retail_store', label: 'Retail Store' },
    { value: 'cold_storage', label: 'Cold Storage' },
    { value: 'hazmat_facility', label: 'Hazmat Facility' },
    { value: 'transit_hub', label: 'Transit Hub' }
  ];

  const operatingModeOptions = [
    { value: 'owned', label: 'Owned (RISE / Branch)' },
    { value: 'franchise', label: 'Franchise (Independent Center)' }
  ];

  const unitOptions = [
    { value: 'sqft', label: 'Square Feet' },
    { value: 'sqm', label: 'Square Meters' },
    { value: 'pallets', label: 'Pallets' },
    { value: 'cbft', label: 'Cubic Feet' },
    { value: 'cbm', label: 'Cubic Meters' }
  ];

  const weightUnitOptions = [
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'lbs', label: 'Pounds (lbs)' },
    { value: 'tons', label: 'Tons' },
    { value: 'mt', label: 'Metric Tons (MT)' }
  ];

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/30 z-50 flex flex-col">
      <div className="relative w-full h-full bg-white shadow-2xl flex flex-col">
        {/* Compact Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <MapPinIcon className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                {isEdit ? 'Edit Location' : 'Create Location'}
              </h3>
            </div>
            <button
              onClick={onCancel}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Error Summary */}
        {Object.keys(errors).length > 0 && (
          <div className="flex-shrink-0 mx-4 mt-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 mb-1">Please fix the following errors:</p>
                <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
                  {Object.entries(errors).map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <form id="location-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Main Form Grid - Compact 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column - Basic Information */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">Basic Information</h4>
                
                {/* Location Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-1">
                    Location Name *
                  </label>
                  <TextInput
                    variant="input"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Main Distribution Center"
                    required
                    className={errors.name ? 'ring-2 ring-red-500' : ''}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>

                {/* Location Code */}
                <div>
                  <label htmlFor="code" className="block text-sm font-semibold text-gray-800 mb-1">
                    Location Code *
                  </label>
                  <TextInput
                    variant="input"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="e.g., LOC-001"
                    required
                    className={`uppercase ${errors.code ? 'ring-2 ring-red-500' : ''}`}
                  />
                  {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code}</p>}
                </div>

                {/* Tax invoice entity code (QQQQ) */}
                <div>
                  <label htmlFor="taxInvoiceEntityCode" className="block text-sm font-semibold text-gray-800 mb-1">
                    Tax invoice entity code (QQQQ)
                  </label>
                  <TextInput
                    variant="input"
                    id="taxInvoiceEntityCode"
                    name="taxInvoiceEntityCode"
                    value={formData.taxInvoiceEntityCode}
                    onChange={handleChange}
                    placeholder="e.g., HQ01"
                    maxLength={15}
                    className={`uppercase ${errors.taxInvoiceEntityCode ? 'ring-2 ring-red-500' : ''}`}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional. Used in regulated tax invoice numbers for this location. Falls back to location code or HQ01.
                  </p>
                  {errors.taxInvoiceEntityCode && (
                    <p className="mt-1 text-xs text-red-600">{errors.taxInvoiceEntityCode}</p>
                  )}
                </div>

                {/* Type */}
                <div>
                  <label htmlFor="type" className="block text-sm font-semibold text-gray-800 mb-1">
                    Location Type *
                  </label>
                  <CustomSelect
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={(e) => {
                      handleChange(e);
                      if (e.target.value === 'franchise_center') {
                        setFormData((prev) => ({ ...prev, type: 'franchise_center', operatingMode: 'franchise' }));
                      }
                    }}
                    options={typeOptions}
                    size="md"
                    variant="default"
                  />
                </div>

                {/* Operating Mode */}
                <div>
                  <label htmlFor="operatingMode" className="block text-sm font-semibold text-gray-800 mb-1">
                    Operating Mode *
                  </label>
                  <CustomSelect
                    id="operatingMode"
                    name="operatingMode"
                    value={formData.operatingMode}
                    onChange={handleChange}
                    options={operatingModeOptions}
                    size="md"
                    variant="default"
                    disabled={formData.type === 'franchise_center'}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Franchise centers are always independent; owned is for RISE HQ and branches.
                  </p>
                </div>

                {(formData.type === 'franchise_center' || formData.operatingMode === 'franchise') && (
                  <div className="space-y-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-indigo-800">Partner / Center Details</h5>
                    <TextInput
                      variant="input"
                      name="partner.legalName"
                      value={formData.partner?.legalName || ''}
                      onChange={handleChange}
                      placeholder="Legal / partner name"
                    />
                    <TextInput
                      variant="input"
                      name="partner.contactName"
                      value={formData.partner?.contactName || ''}
                      onChange={handleChange}
                      placeholder="Contact name"
                    />
                    <TextInput
                      variant="input"
                      name="partner.contactEmail"
                      value={formData.partner?.contactEmail || ''}
                      onChange={handleChange}
                      placeholder="Contact email"
                    />
                    <TextInput
                      variant="input"
                      name="partner.contactPhone"
                      value={formData.partner?.contactPhone || ''}
                      onChange={handleChange}
                      placeholder="Contact phone"
                    />
                    <TextInput
                      variant="input"
                      name="partner.agreementRef"
                      value={formData.partner?.agreementRef || ''}
                      onChange={handleChange}
                      placeholder="Agreement reference"
                    />
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-indigo-800 pt-2">
                      Center bank account (student transfers)
                    </h5>
                    <p className="text-xs text-indigo-700/80">
                      Online card gateway is disabled for franchise centers. Students transfer to this account.
                    </p>
                    <TextInput
                      variant="input"
                      name="partner.bank.bankName"
                      value={formData.partner?.bank?.bankName || ''}
                      onChange={handleChange}
                      placeholder="Bank name"
                    />
                    <TextInput
                      variant="input"
                      name="partner.bank.branchName"
                      value={formData.partner?.bank?.branchName || ''}
                      onChange={handleChange}
                      placeholder="Branch name"
                    />
                    <TextInput
                      variant="input"
                      name="partner.bank.accountName"
                      value={formData.partner?.bank?.accountName || ''}
                      onChange={handleChange}
                      placeholder="Account holder name"
                    />
                    <TextInput
                      variant="input"
                      name="partner.bank.accountNumber"
                      value={formData.partner?.bank?.accountNumber || ''}
                      onChange={handleChange}
                      placeholder="Account number"
                    />
                    <TextInput
                      variant="input"
                      name="partner.bank.bankCode"
                      value={formData.partner?.bank?.bankCode || ''}
                      onChange={handleChange}
                      placeholder="Bank / SWIFT code (optional)"
                    />
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-indigo-800 pt-2">
                      Card collection COA (GL only)
                    </h5>
                    <p className="text-xs text-indigo-700/80">
                      Ledger mapping for future student card collections at this center. Not a payment gateway —
                      student card payments stay disabled on public links.
                    </p>
                    <TextInput
                      variant="input"
                      name="partner.cardCollectionCoa.accountCode"
                      value={formData.partner?.cardCollectionCoa?.accountCode || ''}
                      onChange={handleChange}
                      placeholder="Account code (e.g. 1200)"
                    />
                    <TextInput
                      variant="input"
                      name="partner.cardCollectionCoa.accountName"
                      value={formData.partner?.cardCollectionCoa?.accountName || ''}
                      onChange={handleChange}
                      placeholder="Account name"
                    />
                  </div>
                )}

                {/* Parent Location */}
                <div>
                  <label htmlFor="parentLocation" className="block text-sm font-semibold text-gray-800 mb-1">
                    Parent Location
                  </label>
                  <CustomSelect
                    id="parentLocation"
                    name="parentLocation"
                    value={formData.parentLocation}
                    onChange={handleChange}
                    options={[
                      { value: '', label: '-- No Parent (Top Level) --' },
                      ...locations.map(loc => ({
                        value: loc._id,
                        label: `${loc.name} (${loc.code}) - ${loc.type}`
                      }))
                    ]}
                    size="md"
                    variant="default"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Optional: Select a parent location to create a hierarchy
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-800 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of the location..."
                    className={`block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all resize-none ${
                      errors.description 
                        ? 'ring-red-500 focus:ring-red-500 bg-red-50' 
                        : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white'
                    }`}
                  />
                  {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
                  <p className="mt-1 text-xs text-gray-400">{formData.description?.length || 0}/1000</p>
                </div>

                {/* Active Status */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <input
                      id="settings.isActive"
                      name="settings.isActive"
                      type="checkbox"
                      checked={formData.settings.isActive}
                      onChange={handleWarehouseSettingChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="settings.isActive" className="ml-2 block text-sm font-medium text-gray-900">
                      Active Location
                    </label>
                  </div>
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    formData.settings.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {formData.settings.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>

              {/* Middle Column - Address & Contact */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">Address & Contact</h4>
                
                {/* Street Address */}
                <div>
                  <label htmlFor="address.street" className="block text-sm font-semibold text-gray-800 mb-1">
                    Street Address
                  </label>
                  <TextInput
                    variant="input"
                    id="address.street"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleChange}
                    placeholder="123 Warehouse Blvd"
                  />
                </div>

                {/* City & State */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="address.city" className="block text-sm font-semibold text-gray-800 mb-1">
                      City
                    </label>
                    <TextInput
                      variant="input"
                      id="address.city"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleChange}
                      placeholder="Los Angeles"
                    />
                  </div>
                  <div>
                    <label htmlFor="address.state" className="block text-sm font-semibold text-gray-800 mb-1">
                      State
                    </label>
                    <TextInput
                      variant="input"
                      id="address.state"
                      name="address.state"
                      value={formData.address.state}
                      onChange={handleChange}
                      placeholder="CA"
                    />
                  </div>
                </div>

                {/* ZIP & Country */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="address.zipCode" className="block text-sm font-semibold text-gray-800 mb-1">
                      ZIP/Postal Code
                    </label>
                    <TextInput
                      variant="input"
                      id="address.zipCode"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleChange}
                      placeholder="90210"
                    />
                  </div>
                  <div>
                    <label htmlFor="address.country" className="block text-sm font-semibold text-gray-800 mb-1">
                      Country
                    </label>
                    <TextInput
                      variant="input"
                      id="address.country"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleChange}
                      placeholder="United States"
                    />
                  </div>
                </div>

                {/* Coordinates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="address.coordinates.latitude" className="block text-sm font-semibold text-gray-800 mb-1">
                      Latitude
                    </label>
                    <TextInput
                      variant="input"
                      type="number"
                      step="any"
                      id="address.coordinates.latitude"
                      name="address.coordinates.latitude"
                      value={formData.address.coordinates.latitude}
                      onChange={handleChange}
                      placeholder="40.7128"
                    />
                  </div>
                  <div>
                    <label htmlFor="address.coordinates.longitude" className="block text-sm font-semibold text-gray-800 mb-1">
                      Longitude
                    </label>
                    <TextInput
                      variant="input"
                      type="number"
                      step="any"
                      id="address.coordinates.longitude"
                      name="address.coordinates.longitude"
                      value={formData.address.coordinates.longitude}
                      onChange={handleChange}
                      placeholder="-74.0060"
                    />
                  </div>
                </div>

                {/* Manager */}
                <div>
                  <label htmlFor="contact.manager.name" className="block text-sm font-semibold text-gray-800 mb-1">
                    Location Manager
                  </label>
                  <TextInput
                    variant="input"
                    id="contact.manager.name"
                    name="contact.manager.name"
                    value={formData.contact.manager.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                  />
                </div>

                {/* Manager Email & Phone */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="contact.manager.email" className="block text-sm font-semibold text-gray-800 mb-1">
                      Manager Email
                    </label>
                    <TextInput
                      variant="input"
                      type="email"
                      id="contact.manager.email"
                      name="contact.manager.email"
                      value={formData.contact.manager.email}
                      onChange={handleChange}
                      placeholder="manager@company.com"
                      className={errors['contact.manager.email'] ? 'ring-2 ring-red-500' : ''}
                    />
                    {errors['contact.manager.email'] && <p className="mt-1 text-xs text-red-600">{errors['contact.manager.email']}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact.manager.phone" className="block text-sm font-semibold text-gray-800 mb-1">
                      Manager Phone
                    </label>
                    <TextInput
                      variant="input"
                      type="tel"
                      id="contact.manager.phone"
                      name="contact.manager.phone"
                      value={formData.contact.manager.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* Main Email & Phone */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="contact.email" className="block text-sm font-semibold text-gray-800 mb-1">
                      Email
                    </label>
                    <TextInput
                      variant="input"
                      type="email"
                      id="contact.email"
                      name="contact.email"
                      value={formData.contact.email}
                      onChange={handleChange}
                      placeholder="location@company.com"
                      className={errors['contact.email'] ? 'ring-2 ring-red-500' : ''}
                    />
                    {errors['contact.email'] && <p className="mt-1 text-xs text-red-600">{errors['contact.email']}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact.phone" className="block text-sm font-semibold text-gray-800 mb-1">
                      Phone
                    </label>
                    <TextInput
                      variant="input"
                      type="tel"
                      id="contact.phone"
                      name="contact.phone"
                      value={formData.contact.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                {/* Alternate Phone & Website */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="contact.alternatePhone" className="block text-sm font-semibold text-gray-800 mb-1">
                      Alternate Phone
                    </label>
                    <TextInput
                      variant="input"
                      type="tel"
                      id="contact.alternatePhone"
                      name="contact.alternatePhone"
                      value={formData.contact.alternatePhone}
                      onChange={handleChange}
                      placeholder="+1 (555) 999-9999"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact.website" className="block text-sm font-semibold text-gray-800 mb-1">
                      Website
                    </label>
                    <TextInput
                      variant="input"
                      type="url"
                      id="contact.website"
                      name="contact.website"
                      value={formData.contact.website}
                      onChange={handleChange}
                      placeholder="www.company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Capacity & Settings */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  {['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'].includes(formData.type) 
                    ? 'Warehouse Configuration' 
                    : 'Notes'}
                </h4>
                
                {['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'].includes(formData.type) ? (
                  <>
                    {/* Capacity Fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="capacity.totalSpace" className="block text-sm font-semibold text-gray-800 mb-1">
                          Total Space
                        </label>
                        <TextInput
                          variant="input"
                          type="number"
                          step="0.01"
                          min="0"
                          id="capacity.totalSpace"
                          name="capacity.totalSpace"
                          value={formData.capacity.totalSpace}
                          onChange={handleWarehouseSettingChange}
                          placeholder="10000"
                          className={errors['capacity.totalSpace'] ? 'ring-2 ring-red-500' : ''}
                        />
                        {errors['capacity.totalSpace'] && <p className="mt-1 text-xs text-red-600">{errors['capacity.totalSpace']}</p>}
                      </div>
                      <div>
                        <label htmlFor="capacity.unit" className="block text-sm font-semibold text-gray-800 mb-1">
                          Unit
                        </label>
                        <CustomSelect
                          id="capacity.unit"
                          name="capacity.unit"
                          value={formData.capacity.unit}
                          onChange={handleWarehouseSettingChange}
                          options={unitOptions}
                          size="md"
                          variant="default"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="capacity.usedSpace" className="block text-sm font-semibold text-gray-800 mb-1">
                          Used Space
                        </label>
                        <TextInput
                          variant="input"
                          type="number"
                          step="0.01"
                          min="0"
                          id="capacity.usedSpace"
                          name="capacity.usedSpace"
                          value={formData.capacity.usedSpace}
                          onChange={handleWarehouseSettingChange}
                          placeholder="5000"
                          className={errors['capacity.usedSpace'] ? 'ring-2 ring-red-500' : ''}
                        />
                        {errors['capacity.usedSpace'] && <p className="mt-1 text-xs text-red-600">{errors['capacity.usedSpace']}</p>}
                      </div>
                      <div>
                        <label htmlFor="capacity.availableSpace" className="block text-sm font-semibold text-gray-800 mb-1">
                          Available Space
                        </label>
                        <TextInput
                          variant="input"
                          type="number"
                          step="0.01"
                          min="0"
                          id="capacity.availableSpace"
                          name="capacity.availableSpace"
                          value={formData.capacity.availableSpace}
                          onChange={handleWarehouseSettingChange}
                          placeholder="5000"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="capacity.maxWeight" className="block text-sm font-semibold text-gray-800 mb-1">
                          Max Weight
                        </label>
                        <TextInput
                          variant="input"
                          type="number"
                          step="0.01"
                          min="0"
                          id="capacity.maxWeight"
                          name="capacity.maxWeight"
                          value={formData.capacity.maxWeight}
                          onChange={handleWarehouseSettingChange}
                          placeholder="50000"
                          className={errors['capacity.maxWeight'] ? 'ring-2 ring-red-500' : ''}
                        />
                        {errors['capacity.maxWeight'] && <p className="mt-1 text-xs text-red-600">{errors['capacity.maxWeight']}</p>}
                      </div>
                      <div>
                        <label htmlFor="capacity.weightUnit" className="block text-sm font-semibold text-gray-800 mb-1">
                          Weight Unit
                        </label>
                        <CustomSelect
                          id="capacity.weightUnit"
                          name="capacity.weightUnit"
                          value={formData.capacity.weightUnit}
                          onChange={handleWarehouseSettingChange}
                          options={weightUnitOptions}
                          size="md"
                          variant="default"
                        />
                      </div>
                    </div>

                    {/* Settings */}
                    <div className="pt-2">
                      <h5 className="text-sm font-semibold text-gray-800 mb-3">Settings</h5>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <input
                              id="settings.isDefault"
                              name="settings.isDefault"
                              type="checkbox"
                              checked={formData.settings.isDefault}
                              onChange={handleWarehouseSettingChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="settings.isDefault" className="ml-2 block text-sm font-medium text-gray-900">
                              Default Warehouse
                            </label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <input
                              id="settings.allowNegativeStock"
                              name="settings.allowNegativeStock"
                              type="checkbox"
                              checked={formData.settings.allowNegativeStock}
                              onChange={handleWarehouseSettingChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="settings.allowNegativeStock" className="ml-2 block text-sm font-medium text-gray-900">
                              Allow Negative Stock
                            </label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <input
                              id="settings.autoReorderEnabled"
                              name="settings.autoReorderEnabled"
                              type="checkbox"
                              checked={formData.settings.autoReorderEnabled}
                              onChange={handleWarehouseSettingChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="settings.autoReorderEnabled" className="ml-2 block text-sm font-medium text-gray-900">
                              Auto Reorder
                            </label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <input
                              id="settings.temperatureControlled"
                              name="settings.temperatureControlled"
                              type="checkbox"
                              checked={formData.settings.temperatureControlled}
                              onChange={handleWarehouseSettingChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="settings.temperatureControlled" className="ml-2 block text-sm font-medium text-gray-900">
                              Temperature Controlled
                            </label>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <input
                              id="settings.hazardousMaterials"
                              name="settings.hazardousMaterials"
                              type="checkbox"
                              checked={formData.settings.hazardousMaterials}
                              onChange={handleWarehouseSettingChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="settings.hazardousMaterials" className="ml-2 block text-sm font-medium text-gray-900">
                              Hazardous Materials
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <label htmlFor="notes" className="block text-sm font-semibold text-gray-800 mb-1">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={12}
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Additional notes about this location..."
                      className={`block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all resize-none ${
                        errors.notes 
                          ? 'ring-red-500 focus:ring-red-500 bg-red-50' 
                          : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white'
                      }`}
                    />
                    {errors.notes && <p className="mt-1 text-xs text-red-600">{errors.notes}</p>}
                    <p className="mt-1 text-xs text-gray-400">{formData.notes?.length || 0}/500</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes for Warehouse Types (shown below warehouse config) */}
            {['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'].includes(formData.type) && (
              <div className="lg:col-span-3 pt-3 border-t border-gray-200">
                <div>
                  <label htmlFor="notes-warehouse" className="block text-sm font-semibold text-gray-800 mb-1">
                    Notes
                  </label>
                  <textarea
                    id="notes-warehouse"
                    name="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Additional notes about this location..."
                    className={`block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all resize-none ${
                      errors.notes 
                        ? 'ring-red-500 focus:ring-red-500 bg-red-50' 
                        : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white'
                    }`}
                  />
                  {errors.notes && <p className="mt-1 text-xs text-red-600">{errors.notes}</p>}
                  <p className="mt-1 text-xs text-gray-400">{formData.notes?.length || 0}/500</p>
                </div>
              </div>
            )}

          </div>
        </form>

        {/* Form Actions - Fixed Footer */}
        <div className="flex-shrink-0 bg-white px-4 py-4 border-t border-gray-200">
          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="location-form"
              disabled={submitting}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:hover:scale-100"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  {isEdit ? 'Update Location' : 'Create Location'}
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationForm;

